import { useState } from 'react';

import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import {
  calculatePayoutPool,
  calculatePrizePreview,
  formatMoney,
  getMinPlayerOptions,
  getPrizeDistributionBuckets,
  getRulePercentageTotal,
  getWinnerCountOptions,
  isPayoutPrizeType,
  parseMoneyValue,
  parsePercentageValue,
  payoutPercentageOptions,
  prizeTypeOptions,
} from '../utils/competitionFinance';

const buildFinnishReferenceNumber = (baseNumber) => {
  const digits = String(baseNumber || '').replace(/\D+/g, '').replace(/^0+/, '');

  if (!digits) {
    return '';
  }

  const normalizedDigits = digits.length < 3 ? digits.padEnd(3, '1') : digits.slice(0, 5);
  const weights = [7, 3, 1];
  let sum = 0;
  let weightIndex = 0;

  for (let index = normalizedDigits.length - 1; index >= 0; index -= 1) {
    sum += Number(normalizedDigits[index]) * weights[weightIndex % 3];
    weightIndex += 1;
  }

  const checkDigit = (10 - (sum % 10)) % 10;
  return `${normalizedDigits}${checkDigit}`;
};

const isValidFinnishReferenceNumber = (referenceNumber) => {
  const digits = String(referenceNumber || '').replace(/\D+/g, '');

  if (digits.length < 4 || digits.length > 20 || digits.startsWith('0')) {
    return false;
  }

  const base = digits.slice(0, -1);
  const checkDigit = Number(digits.slice(-1));
  const weights = [7, 3, 1];
  let sum = 0;

  for (let index = base.length - 1, weightIndex = 0; index >= 0; index -= 1, weightIndex += 1) {
    sum += Number(base[index]) * weights[weightIndex % 3];
  }

  return ((10 - (sum % 10)) % 10) === checkDigit;
};

const resolveBankPaymentMode = ({ usePersonalReferenceNumber, bankPaymentBarcode, bankPaymentMessage }) => {
  if (usePersonalReferenceNumber) {
    return 'personalReference';
  }

  if (String(bankPaymentBarcode || '').trim()) {
    return 'reference';
  }

  if (String(bankPaymentMessage || '').trim()) {
    return 'message';
  }

  return 'message';
};

function CompetitionFinanceTab({
  useClassSpecificEntryFee,
  onFeeModeChange,
  entryFee,
  onEntryFeeChange,
  classes,
  onClassEntryFeeChange,
  enableBankPayment,
  onEnableBankPaymentChange,
  bankAccount,
  onBankAccountChange,
  bankRecipientName,
  onBankRecipientNameChange,
  enableMobilepayPayment,
  onEnableMobilepayPaymentChange,
  mobilepayNumber,
  onMobilepayNumberChange,
  bankPaymentMessage,
  onBankPaymentMessageChange,
  bankPaymentBarcode,
  onBankPaymentBarcodeChange,
  usePersonalReferenceNumber,
  onUsePersonalReferenceNumberChange,
  mobilepayMessage,
  onMobilepayMessageChange,
  prizeSettings,
  onPrizeSettingChange,
  onPrizeRuleChange,
  onAddPrizeRule,
  onRemovePrizeRule,
}) {
  const showPayoutRules = isPayoutPrizeType(prizeSettings.prizeType);
  const [bankPaymentMode, setBankPaymentMode] = useState(resolveBankPaymentMode({
    usePersonalReferenceNumber,
    bankPaymentBarcode,
    bankPaymentMessage,
  }));
  const hasInvalidSharedReference = bankPaymentMode === 'reference'
    && bankPaymentBarcode.trim() !== ''
    && !isValidFinnishReferenceNumber(bankPaymentBarcode);

  const handleBankPaymentModeChange = (nextMode) => {
    setBankPaymentMode(nextMode);
    onUsePersonalReferenceNumberChange(nextMode === 'personalReference');

    if (nextMode !== 'message') {
      onBankPaymentMessageChange('');
    }

    if (nextMode !== 'reference') {
      onBankPaymentBarcodeChange('');
    }

    if (nextMode === 'personalReference') {
      onMobilepayMessageChange('');
    }
  };

  const getPreviewBases = (rule) => {
    const minPlayers = Number(rule?.minPlayers || 0);

    if (!minPlayers) {
      return [];
    }

    if (!useClassSpecificEntryFee) {
      const parsedEntryFee = parseMoneyValue(entryFee);

      if (parsedEntryFee === null) {
        return [];
      }

      return [{
        key: 'shared',
        label: 'Yhteinen maksu',
        totalEntryFees: Math.round(parsedEntryFee * minPlayers * 100) / 100,
      }];
    }

    return classes
      .map((competitionClass) => {
        const parsedEntryFee = parseMoneyValue(competitionClass.entryFee);

        if (parsedEntryFee === null) {
          return null;
        }

        return {
          key: competitionClass.id,
          label: competitionClass.name || 'Nimetön luokka',
          totalEntryFees: Math.round(parsedEntryFee * minPlayers * 100) / 100,
        };
      })
      .filter(Boolean);
  };

  const handleGenerateReferenceNumber = () => {
    const currentDigits = String(bankPaymentBarcode || '').replace(/\D+/g, '').replace(/^0+/, '');
    const nextReference = currentDigits && !isValidFinnishReferenceNumber(currentDigits)
      ? buildFinnishReferenceNumber(currentDigits)
      : buildFinnishReferenceNumber(`${Date.now()}`.slice(-5));

    onBankPaymentBarcodeChange(nextReference);
  };

  return (
    <div className="space-y-6">
      <section className="rounded-[24px] border border-white/10 bg-white/5 p-4">
        <div className="border-b border-white/10 pb-4 text-left">
          <h2 className="text-xl font-bold text-white">Maksut</h2>
          <p className="mt-2 text-sm leading-6 text-slate-300">
            Määritä osallistumismaksu yhteisenä tai luokkakohtaisena ja tarkista kilpailulle kopioidut maksutiedot.
          </p>
        </div>

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2 grid gap-3 md:grid-cols-2">
            <button
              type="button"
              onClick={() => onFeeModeChange(false)}
              className={`rounded-2xl border px-4 py-4 text-left transition ${
                !useClassSpecificEntryFee
                  ? 'border-cyan-300 bg-cyan-400/15 text-white'
                  : 'border-white/10 bg-white/5 text-slate-300 hover:border-white/20 hover:bg-white/10'
              }`}
            >
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-300">Yhteinen maksu</p>
              <p className="mt-2 text-sm leading-6">Käytä yhtä osallistumismaksua kaikille luokille.</p>
            </button>
            <button
              type="button"
              onClick={() => onFeeModeChange(true)}
              className={`rounded-2xl border px-4 py-4 text-left transition ${
                useClassSpecificEntryFee
                  ? 'border-cyan-300 bg-cyan-400/15 text-white'
                  : 'border-white/10 bg-white/5 text-slate-300 hover:border-white/20 hover:bg-white/10'
              }`}
            >
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-300">Luokkakohtainen maksu</p>
              <p className="mt-2 text-sm leading-6">Aseta erillinen osallistumismaksu jokaiselle luokalle.</p>
            </button>
          </div>

          {!useClassSpecificEntryFee ? (
            <TextField
              variant="filled"
              label="Osallistumismaksu (EUR)"
              type="number"
              value={entryFee}
              inputProps={{ min: 0, step: 0.01 }}
              sx={{ width: 1, backgroundColor: 'white', borderRadius: '12px' }}
              onChange={(event) => onEntryFeeChange(event.target.value)}
            />
          ) : (
            <div className="md:col-span-2 overflow-hidden rounded-2xl border border-white/10">
              <table className="min-w-full divide-y divide-white/10 text-left text-sm text-slate-200">
                <thead className="bg-white/10 text-xs uppercase tracking-[0.2em] text-cyan-300">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Luokka</th>
                    <th className="px-4 py-3 font-semibold">Osallistumismaksu</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10 bg-slate-950/20">
                  {classes.length ? classes.map((competitionClass) => (
                    <tr key={competitionClass.id}>
                      <td className="px-4 py-3 font-semibold text-white">{competitionClass.name || 'Nimetön luokka'}</td>
                      <td className="px-4 py-3">
                        <TextField
                          variant="filled"
                          label="EUR"
                          type="number"
                          value={competitionClass.entryFee ?? ''}
                          inputProps={{ min: 0, step: 0.01 }}
                          sx={{ width: { xs: 1, sm: 220 }, backgroundColor: 'white', borderRadius: '12px' }}
                          onChange={(event) => onClassEntryFeeChange(competitionClass.id, event.target.value)}
                        />
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="2" className="px-4 py-6 text-center text-slate-400">
                        Lisää ensin vähintään yksi luokka, jotta voit asettaa luokkakohtaiset maksut.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="md:col-span-2 rounded-2xl border border-white/10 bg-slate-950/20 p-4">
            <div className="border-b border-white/10 pb-4 text-left">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-300">Tilille maksu</p>
              <p className="mt-2 text-sm leading-6 text-slate-300">
                Valitse näytetäänkö tilimaksu kilpailun maksutiedoissa. Tilisiirrolle voi määrittää viestin, yhteisen viitenumeron tai henkilökohtaiset viitteet.
              </p>
            </div>

            <div className="mt-4">
              <FormControlLabel
                control={<Checkbox checked={enableBankPayment} onChange={(event) => onEnableBankPaymentChange(event.target.checked)} sx={{ color: 'rgba(103, 232, 249, 0.7)', '&.Mui-checked': { color: '#67e8f9' } }} />}
                label={<span className="text-sm font-semibold text-white">Maksu tilille</span>}
              />
            </div>

            {enableBankPayment ? (
              <>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <TextField
                    variant="filled"
                    label="Tilinumero"
                    value={bankAccount}
                    sx={{ width: 1, backgroundColor: 'white', borderRadius: '12px' }}
                    onChange={(event) => onBankAccountChange(event.target.value)}
                  />

                  <div className="flex items-center gap-2">
                    <TextField
                      variant="filled"
                      label="Tilisiirron saaja"
                      value={bankRecipientName}
                      sx={{ width: 1, backgroundColor: 'white', borderRadius: '12px' }}
                      onChange={(event) => onBankRecipientNameChange(event.target.value)}
                    />
                    {bankRecipientName?.trim() && (
                      <button
                        type="button"
                        title="Kopioi saajan nimi"
                        className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-slate-700 hover:bg-slate-100"
                        onClick={() => navigator.clipboard?.writeText(bankRecipientName)}
                      >
                        Kopioi
                      </button>
                    )}
                  </div>
                </div>

                <div className="mt-4 grid gap-3 md:grid-cols-3">
                  <button
                    type="button"
                    onClick={() => handleBankPaymentModeChange('message')}
                    className={`rounded-2xl border px-4 py-4 text-left transition ${
                      bankPaymentMode === 'message'
                        ? 'border-cyan-300 bg-cyan-400/15 text-white'
                        : 'border-white/10 bg-white/5 text-slate-300 hover:border-white/20 hover:bg-white/10'
                    }`}
                  >
                    <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-300">Viesti</p>
                    <p className="mt-2 text-sm leading-6">Näytä tilisiirtoon maksuviesti.</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleBankPaymentModeChange('reference')}
                    className={`rounded-2xl border px-4 py-4 text-left transition ${
                      bankPaymentMode === 'reference'
                        ? 'border-cyan-300 bg-cyan-400/15 text-white'
                        : 'border-white/10 bg-white/5 text-slate-300 hover:border-white/20 hover:bg-white/10'
                    }`}
                  >
                    <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-300">Viitenumero</p>
                    <p className="mt-2 text-sm leading-6">Käytä samaa suomalaista viitenumeroa kaikilla maksuilla.</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleBankPaymentModeChange('personalReference')}
                    className={`rounded-2xl border px-4 py-4 text-left transition ${
                      bankPaymentMode === 'personalReference'
                        ? 'border-cyan-300 bg-cyan-400/15 text-white'
                        : 'border-white/10 bg-white/5 text-slate-300 hover:border-white/20 hover:bg-white/10'
                    }`}
                  >
                    <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-300">Henkilökohtainen viite</p>
                    <p className="mt-2 text-sm leading-6">Luo jokaiselle pelaajalle suomalainen viitenumero.</p>
                  </button>
                </div>

                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  {bankPaymentMode === 'message' ? (
                    <TextField
                      variant="filled"
                      label="Tilisiirron viesti"
                      value={bankPaymentMessage}
                      sx={{ width: 1, backgroundColor: 'white', borderRadius: '12px' }}
                      onChange={(event) => onBankPaymentMessageChange(event.target.value)}
                      fullWidth
                      className="md:col-span-2"
                    />
                  ) : null}

                  {bankPaymentMode === 'reference' ? (
                    <div className="md:col-span-2 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
                      <TextField
                        variant="filled"
                        label="Yhteinen viitenumero"
                        value={bankPaymentBarcode}
                        sx={{ width: 1, backgroundColor: 'white', borderRadius: '12px' }}
                        onChange={(event) => onBankPaymentBarcodeChange(event.target.value.replace(/\D+/g, '').replace(/^0+/, ''))}
                        error={hasInvalidSharedReference}
                        helperText={hasInvalidSharedReference ? 'Viitenumero ei ole validi suomalainen viitenumero. Käytä Generoi laskeaksesi tarkistemerkin.' : 'Anna validi suomalainen viitenumero tai generoi uusi.'}
                        fullWidth
                      />
                      <button
                        type="button"
                        onClick={handleGenerateReferenceNumber}
                        className="inline-flex h-[56px] items-center justify-center rounded-2xl border border-cyan-300 bg-cyan-400/15 px-4 text-sm font-semibold text-cyan-100 transition hover:bg-cyan-400/25"
                      >
                        Generoi
                      </button>
                    </div>
                  ) : null}

                  {bankPaymentMode === 'personalReference' ? (
                    <div className="md:col-span-2 rounded-2xl border border-cyan-300/30 bg-cyan-400/10 px-4 py-4 text-sm leading-6 text-slate-200">
                      Pelaajalle luodaan rekisteröitymisen yhteydessä henkilökohtainen suomalainen viitenumero. Sama viitenumero näytetään julkisissa maksutiedoissa sekä tilisiirtoa että MobilePayta varten.
                    </div>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>

          <div className="md:col-span-2 rounded-2xl border border-white/10 bg-slate-950/20 p-4">
            <div className="border-b border-white/10 pb-4 text-left">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-300">MobilePay</p>
              <p className="mt-2 text-sm leading-6 text-slate-300">
                Valitse näytetäänkö MobilePay-maksu kilpailun maksutiedoissa. MobilePay-viestiä käytetään vain silloin kun henkilökohtaisia viitteitä ei ole käytössä.
              </p>
            </div>

            <div className="mt-4">
              <FormControlLabel
                control={<Checkbox checked={enableMobilepayPayment} onChange={(event) => onEnableMobilepayPaymentChange(event.target.checked)} sx={{ color: 'rgba(103, 232, 249, 0.7)', '&.Mui-checked': { color: '#67e8f9' } }} />}
                label={<span className="text-sm font-semibold text-white">MobilePay maksu</span>}
              />
            </div>

            {enableMobilepayPayment ? (
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <TextField
                  variant="filled"
                  label="MobilePay-numero"
                  value={mobilepayNumber}
                  sx={{ width: 1, backgroundColor: 'white', borderRadius: '12px' }}
                  onChange={(event) => onMobilepayNumberChange(event.target.value)}
                />

                <TextField
                  variant="filled"
                  label={bankPaymentMode === 'personalReference' ? 'MobilePay-viesti poistuu käytöstä' : 'MobilePay-viesti'}
                  value={bankPaymentMode === 'personalReference' ? '' : mobilepayMessage}
                  sx={{ width: 1, backgroundColor: 'white', borderRadius: '12px' }}
                  onChange={(event) => onMobilepayMessageChange(event.target.value)}
                  disabled={bankPaymentMode === 'personalReference'}
                  helperText={bankPaymentMode === 'personalReference' ? 'Henkilökohtainen viitenumero korvaa MobilePay-viestin.' : 'Näytetään kilpailun julkisissa maksutiedoissa, jos täytetty.'}
                  fullWidth
                  className="md:col-span-2"
                />
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <section className="rounded-[24px] border border-white/10 bg-white/5 p-4">
        <div className="flex flex-col gap-3 border-b border-white/10 pb-4 md:flex-row md:items-center md:justify-between">
          <div className="text-left">
            <h2 className="text-xl font-bold text-white">Palkinnot</h2>
            <p className="mt-2 text-sm leading-6 text-slate-300">
              Palkintoasetukset ovat yhteiset kaikille luokille, mutta toteutuva jako määräytyy luokkakohtaisen pelaajamäärän perusteella.
            </p>
          </div>
          {showPayoutRules ? <Button type="button" variant="contained" color="primary" onClick={onAddPrizeRule}>Lisää sääntö</Button> : null}
        </div>

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <FormControl variant="filled" fullWidth>
            <InputLabel id="competition-prize-type-label">Palkinnon tyyppi</InputLabel>
            <Select
              labelId="competition-prize-type-label"
              value={prizeSettings.prizeType}
              onChange={(event) => onPrizeSettingChange('prizeType', event.target.value)}
              sx={{ backgroundColor: 'white', borderRadius: '12px' }}
            >
              {prizeTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {showPayoutRules ? (
            <FormControl variant="filled" fullWidth>
              <InputLabel id="competition-payout-label">Payout (% osallistumismaksuista)</InputLabel>
              <Select
                labelId="competition-payout-label"
                value={prizeSettings.payoutAmount === '' ? '' : String(prizeSettings.payoutAmount)}
                onChange={(event) => onPrizeSettingChange('payoutAmount', event.target.value)}
                sx={{ backgroundColor: 'white', borderRadius: '12px' }}
              >
                {payoutPercentageOptions.map((option) => (
                  <MenuItem key={option} value={String(option)}>{option} %</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <div className="rounded-2xl border border-dashed border-white/15 bg-white/5 px-5 py-4 text-sm text-slate-300 md:col-span-2">
              Payout ja palkintojen jakosäännöt ovat käytössä vain lahjakorteille ja käteispalkinnoille.
            </div>
          )}
        </div>

        {showPayoutRules ? (
          <div className="mt-5 space-y-4">
            {prizeSettings.rules.length ? prizeSettings.rules.map((rule) => {
              const buckets = getPrizeDistributionBuckets(rule.winnerCount);
              const minPlayerOptions = getMinPlayerOptions(rule.minPlayers);
              const selectedMinimums = new Set(
                prizeSettings.rules
                  .filter((candidateRule) => candidateRule.id !== rule.id)
                  .map((candidateRule) => Number(candidateRule.minPlayers || 0))
                  .filter((value) => value > 0)
              );
              const winnerCountOptions = getWinnerCountOptions(rule.minPlayers);
              const percentageTotal = getRulePercentageTotal(rule);
              const previewBases = getPreviewBases(rule);
              const hasDuplicateMinimum = selectedMinimums.has(Number(rule.minPlayers || 0));

              return (
                <div key={rule.id} className="rounded-2xl border border-white/10 bg-slate-950/20 p-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div className="grid flex-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                      <FormControl variant="filled" fullWidth error={hasDuplicateMinimum}>
                        <InputLabel id={`min-players-label-${rule.id}`}>Minimipelaajamäärä</InputLabel>
                        <Select
                          labelId={`min-players-label-${rule.id}`}
                          value={rule.minPlayers}
                          onChange={(event) => onPrizeRuleChange(rule.id, 'minPlayers', event.target.value)}
                          sx={{ backgroundColor: 'white', borderRadius: '12px' }}
                        >
                          {minPlayerOptions.map((option) => (
                            <MenuItem
                              key={option}
                              value={String(option)}
                              disabled={selectedMinimums.has(option)}
                            >
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                        {hasDuplicateMinimum ? <FormHelperText>Sama minimipelaajamäärä on jo käytössä toisessa säännössä.</FormHelperText> : null}
                      </FormControl>

                      <FormControl variant="filled" fullWidth>
                        <InputLabel id={`winner-count-label-${rule.id}`}>Palkittavia pelaajia</InputLabel>
                        <Select
                          labelId={`winner-count-label-${rule.id}`}
                          value={rule.winnerCount}
                          onChange={(event) => onPrizeRuleChange(rule.id, 'winnerCount', Number(event.target.value))}
                          sx={{ backgroundColor: 'white', borderRadius: '12px' }}
                        >
                          {winnerCountOptions.map((option) => (
                            <MenuItem key={option} value={option}>{option}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-300">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">Prosentit yhteensä</p>
                        <p className="mt-2 text-lg font-bold text-white">{percentageTotal} %</p>
                        {percentageTotal < 100 ? (
                          <p className="mt-1 text-xs text-amber-300">Palkintopotista jää jakamatta {100 - percentageTotal} %</p>
                        ) : null}
                      </div>
                    </div>

                    <Button type="button" variant="contained" color="error" onClick={() => onRemovePrizeRule(rule.id)}>Poista</Button>
                  </div>

                  <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    {buckets.map((bucket, bucketIndex) => (
                      <TextField
                        key={`${rule.id}-${bucket.key}`}
                        variant="filled"
                        label={`${bucket.label} (%)`}
                        type="number"
                        value={rule.percentages[bucketIndex] ?? ''}
                        inputProps={{ min: 0, max: 100, step: 1 }}
                        sx={{ width: 1, backgroundColor: 'white', borderRadius: '12px' }}
                        onChange={(event) => onPrizeRuleChange(rule.id, 'percentage', event.target.value, bucketIndex)}
                      />
                    ))}
                  </div>

                  <div className="mt-4 space-y-4">
                    {previewBases.length ? previewBases.map((previewBase) => {
                      const payoutPool = calculatePayoutPool(previewBase.totalEntryFees, prizeSettings.payoutAmount);
                      const previewRows = calculatePrizePreview(rule, prizeSettings.payoutAmount, previewBase.totalEntryFees);

                      return (
                        <div key={`${rule.id}-${previewBase.key}`} className="overflow-hidden rounded-2xl border border-white/10">
                          <div className="grid gap-3 border-b border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-300 md:grid-cols-3">
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">Luokka / pohja</p>
                              <p className="mt-1 font-semibold text-white">{previewBase.label}</p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">Osallistumismaksut minimillä {rule.minPlayers}</p>
                              <p className="mt-1 font-semibold text-white">{formatMoney(previewBase.totalEntryFees)}</p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">Palkintopotti</p>
                              <p className="mt-1 font-semibold text-white">{formatMoney(payoutPool)}</p>
                            </div>
                          </div>

                          <table className="min-w-full divide-y divide-white/10 text-left text-sm text-slate-200">
                            <thead className="bg-white/10 text-xs uppercase tracking-[0.2em] text-cyan-300">
                              <tr>
                                <th className="px-4 py-3 font-semibold">Ryhmä</th>
                                <th className="px-4 py-3 font-semibold">Osuus</th>
                                <th className="px-4 py-3 font-semibold">Summa minimillä {rule.minPlayers}</th>
                                <th className="px-4 py-3 font-semibold">Per pelaaja</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-white/10 bg-slate-950/20">
                              {previewRows.map((previewRow) => (
                                <tr key={`${rule.id}-${previewBase.key}-${previewRow.key}-preview`}>
                                  <td className="px-4 py-3 font-semibold text-white">{previewRow.label}</td>
                                  <td className="px-4 py-3">{parsePercentageValue(previewRow.percentage) ?? 0} %</td>
                                  <td className="px-4 py-3">{formatMoney(previewRow.totalAmount)}</td>
                                  <td className="px-4 py-3">{formatMoney(previewRow.perPlayerAmount)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    }) : (
                      <div className="rounded-2xl border border-dashed border-white/15 bg-white/5 px-6 py-6 text-center text-sm text-slate-300">
                        Aseta osallistumismaksu, jotta todelliset palkintosummat voidaan laskea.
                      </div>
                    )}
                  </div>
                </div>
              );
            }) : (
              <div className="rounded-2xl border border-dashed border-white/15 bg-white/5 px-6 py-8 text-center text-slate-300">
                Lisää yksi tai useampi sääntö eri minimipelaajamäärille. Käytettävä sääntö määräytyy luokan toteutuneen osallistujamäärän mukaan.
              </div>
            )}
          </div>
        ) : null}
      </section>
    </div>
  );
}

export default CompetitionFinanceTab;